<template>
  <div>
    <div v-if="showModal" class="epo-home-notice">
      <div class="epo-home-notice__scrim" aria-hidden="true"></div>
      <div
        class="epo-home-notice__dialog"
        role="dialog"
        aria-modal="true"
        :aria-label="modalTitle"
      >
        <img
          class="epo-home-notice__icon"
          :src="docUploadIcon"
          width="96"
          height="96"
          alt=""
        />

        <div class="epo-home-notice__content">
          <h2 class="epo-home-notice__title">{{ modalTitle }}</h2>
          <p class="epo-home-notice__body" v-html="modalBodyHtml"></p>

          <div v-if="showEntitlements" class="epo-home-notice__entitlements">
            <div class="epo-home-notice__ent-col">
              <div class="epo-home-notice__ent-label">
                {{ currentEntitlementsHeading }}
              </div>
              <p
                v-for="(line, idx) in currentEntitlementLines"
                :key="'c-' + idx"
                class="epo-home-notice__ent-line"
              >
                {{ line }}
              </p>
            </div>
            <div class="epo-home-notice__ent-col">
              <div class="epo-home-notice__ent-label">
                {{ newEntitlementsHeading }}
              </div>
              <p
                v-for="(line, idx) in newEntitlementLines"
                :key="'n-' + idx"
                class="epo-home-notice__ent-line"
              >
                {{ line }}
              </p>
            </div>
          </div>
        </div>

        <div class="epo-home-notice__actions">
          <button type="button" class="epo-home-notice__later" @click="onLater">
            {{ $t('lbl_EMPBE_EPO_HomeModal_RemindLater') }}
          </button>
          <button type="button" class="epo-home-notice__ok" @click="onPrimary">
            {{ primaryLabel }}
          </button>
        </div>
      </div>
    </div>

    <EpoRoleInactiveModal
      v-model="showSelectInactive"
      :excess-count="notice.excessCount"
      :target-role-limit="notice.targetRoleLimit"
      @saved="onSelectInactiveSaved"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { epoService } from '../_services/epo.service';
import EpoRoleInactiveModal from './EpoRoleInactiveModal.vue';
import { BR_LICENSE_ASSETS } from '../constants/brLicenseAssets';
import { normalizeApiResponse } from '../_helpers/apiResponse';

export default {
  name: 'EpoRoleNoticeHost',
  components: { EpoRoleInactiveModal },
  data() {
    return {
      homePath: '/dashboard/default',
      myUsersPath: '/UserSetting/MyUsers',
      legacySessionKey: 'epo_role_notice_dismissed',
      loading: false,
      showModal: false,
      showSelectInactive: false,
      phase: 'None',
      /** Reminder 点 Later：仅当前页不重弹；刷新 / 重新进首页会再提示 */
      dismissedReminderKey: '',
      notice: {
        needNotice: false,
        scheduledDate: '',
        actionDeadline: '',
        fromGrade: '',
        targetGrade: '',
        targetRoleLimit: 0,
        currentRoleLimit: 0,
        currentRoleUsed: 0,
        currentProductQty: 0,
        currentGalleryQty: 0,
        targetProductQty: 0,
        targetGalleryQty: 0,
        excessCount: 0,
        daysRemaining: 0,
        jobClearDays: 7
      },
      docUploadIcon: BR_LICENSE_ASSETS.docUploadIcon,
    };
  },
  computed: {
    ...mapGetters({
      getPermissionJsonByUser: 'responsePermission/getPermissionJsonByUser',
    }),
    /** 与公司信息页相同：登录用户 → SponsorPermission 行 */
    permissionData() {
      const user = this.$store.state.authentication?.user;
      if (!user) return null;
      return this.getPermissionJsonByUser({ user }) || null;
    },
    /** 当前档：优先 Notice.FromGrade（即将到期档），否则 permissionData.Type */
    currentPermissionType() {
      const fromNotice = this.noticeGradeToPermissionType(
        this.notice.fromGrade,
        this.notice.currentRoleLimit,
      );
      if (fromNotice) return fromNotice;
      return this.permissionData?.Type || '';
    },
    /** 目标档 Type：Notice TargetGrade 落到同一套 Type */
    targetPermissionType() {
      return this.noticeGradeToPermissionType(
        this.notice.targetGrade,
        this.notice.targetRoleLimit,
      );
    },
    expiryDateText() {
      return this.formatNoticeDate(this.notice.scheduledDate);
    },
    actionDeadlineText() {
      if (this.notice.actionDeadline) return this.formatNoticeDate(this.notice.actionDeadline);
      const raw = this.notice.scheduledDate;
      if (!raw) return '';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return '';
      d.setDate(d.getDate() + 6);
      return this.formatNoticeDate(d);
    },
    jobClearDateText() {
      const raw = this.notice.scheduledDate;
      if (!raw) return '';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return '';
      d.setDate(d.getDate() + (Number(this.notice.jobClearDays) || 7));
      return this.formatNoticeDate(d);
    },
    fromGradeLabel() {
      return this.permissionTypeLabel(this.currentPermissionType);
    },
    targetGradeLabel() {
      return this.permissionTypeLabel(this.targetPermissionType);
    },
    showEntitlements() {
      return this.phase === 'Reminder';
    },
    currentEntitlementLines() {
      return this.buildEntitlementLines(
        this.notice.currentProductQty,
        this.notice.currentRoleLimit,
      );
    },
    newEntitlementLines() {
      return this.buildEntitlementLines(
        this.notice.targetProductQty,
        this.notice.targetRoleLimit,
      );
    },
    currentEntitlementsHeading() {
      return this.$t('lbl_EMPBE_EPO_HomeModal_CurrentEntitlementsWithGrade', {
        grade: this.fromGradeLabel,
      });
    },
    newEntitlementsHeading() {
      return this.$t('lbl_EMPBE_EPO_HomeModal_NewEntitlementsWithGrade', {
        grade: this.targetGradeLabel,
      });
    },
    modalTitle() {
      if (this.phase === 'Action') {
        return this.$t('lbl_EMPBE_EPO_ActionBanner_Title', {
          count: this.notice.currentRoleUsed || this.notice.excessCount,
          excess: this.notice.excessCount,
          new: this.notice.targetRoleLimit,
          days: this.notice.daysRemaining,
          deadline: this.actionDeadlineText,
          autoDate: this.jobClearDateText,
          date: this.expiryDateText,
        });
      }
      return this.$t('lbl_EMPBE_EPO_HomeModal_ReminderTitle', {
        grade: this.targetGradeLabel,
        days: this.notice.daysRemaining,
      });
    },
    modalBodyHtml() {
      if (this.phase === 'Action') {
        return this.escapeHtml(
          this.$t('lbl_EMPBE_EPO_ActionBanner_Body', {
            count: this.notice.currentRoleUsed || this.notice.excessCount,
            excess: this.notice.excessCount,
            new: this.notice.targetRoleLimit,
            old: this.notice.currentRoleLimit,
            days: this.notice.daysRemaining,
            deadline: this.actionDeadlineText,
            autoDate: this.jobClearDateText,
            date: this.expiryDateText,
          }),
        );
      }
      const before = this.$t('lbl_EMPBE_EPO_HomeModal_ReminderBodyBefore', {
        grade: this.targetGradeLabel,
        fromGrade: this.fromGradeLabel,
      });
      const after = this.$t('lbl_EMPBE_EPO_HomeModal_ReminderBodyAfter', {
        grade: this.targetGradeLabel,
        fromGrade: this.fromGradeLabel,
      });
      return `${this.escapeHtml(before)}<strong>${this.escapeHtml(this.expiryDateText)}</strong>${this.escapeHtml(after)}`;
    },
    primaryLabel() {
      /* Reminder / Action 主按钮均引导去用户管理（角色） */
      return this.$t('lbl_EMPBE_EPO_ActionBanner_ManageUsers');
    },
  },
  watch: {
    '$route.path'(to, from) {
      if (this.isHomeRoute(to)) {
        /* 离开首页再回来：允许 Reminder 再弹 */
        if (from && !this.isHomeRoute(from)) {
          this.dismissedReminderKey = '';
        }
        this.evaluate();
      } else {
        this.showModal = false;
      }
    },
  },
  mounted() {
    try {
      sessionStorage.removeItem(this.legacySessionKey);
    } catch (e) {
      /* ignore */
    }
    this.$nextTick(() => this.evaluate());
  },
  methods: {
    isHomeRoute(path) {
      const p = String(path || '').replace(/\/+$/, '') || '/';
      return p === this.homePath || p === '/dashboard' || p === '/';
    },
    escapeHtml(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    toQty(v) {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },
    formatNoticeDate(raw) {
      if (!raw) return '';
      const d = raw instanceof Date ? raw : new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${yyyy}-${mm}-${dd}`;
    },
    /** Notice 档名 → SponsorPermission.Type（与公司 permissionData 同词） */
    noticeGradeToPermissionType(raw, roleLimit) {
      const g = String(raw || '').trim();
      if (g === 'A+' || g === 'A' || g === 'B' || g === 'C' || g === 'Member' || g === 'Guest') {
        return g;
      }
      const u = g.toUpperCase();
      if (u === 'BASIC' || u === 'MEMBER') return 'Member';
      if (u === 'F' || u === 'FREE' || u === 'GUEST') return 'Guest';
      if (Number(roleLimit) <= 0) return 'Guest';
      return 'Member';
    },
    /** 展示：付费档用 Type 原文；Member/Guest 显示 Basic/Free */
    permissionTypeLabel(type) {
      if (type === 'Member') return this.$t('lbl_EMPBE_EPO_HomeModal_GradeBasic');
      if (type === 'Guest') return this.$t('lbl_EMPBE_EPO_HomeModal_GradeFree');
      return type || '';
    },
    /** 数量一律来自 Notice API（SysPaymentItemPerAccount），前端不硬编码；图库行非必要不展示 */
    buildEntitlementLines(productQty, userLimit) {
      const products = this.toQty(productQty);
      const users = this.toQty(userLimit);
      return [
        this.$t('lbl_EMPBE_EPO_HomeModal_DisplayProducts', { count: products }),
        this.$t('lbl_EMPBE_EPO_HomeModal_ActiveUsers', { count: users }),
      ];
    },
    canPrompt() {
      const user = this.$store?.state?.authentication?.user;
      if (!user) return false;
      if (String(user.userType || '').toUpperCase() === 'ADMIN') return false;
      return this.isHomeRoute(this.$route.path);
    },
    dismissKey() {
      return `${this.phase}|${this.notice.scheduledDate || ''}|${this.notice.excessCount || 0}`;
    },
    isReminderDismissed() {
      return this.dismissedReminderKey === this.dismissKey();
    },
    markReminderDismissed() {
      this.dismissedReminderKey = this.dismissKey();
    },
    async evaluate() {
      if (!this.canPrompt() || this.loading) return;
      this.loading = true;
      try {
        const rawRes = await epoService.GetRoleInactiveNotice();
        const res = normalizeApiResponse(rawRes);
        if (!res.isSuccessful) {
          // eslint-disable-next-line no-console
          console.warn('[EPO Notice] API failed', res.systemErrorMsg || res.msg, rawRes);
          return;
        }
        const row = Array.isArray(res.data) ? res.data[0] : res.data;
        if (!row) {
          // eslint-disable-next-line no-console
          console.warn('[EPO Notice] empty data', rawRes);
          return;
        }

        const needNotice = !!(row.needNotice ?? row.NeedNotice);
        const phase = String(row.phase ?? row.Phase ?? 'None');
        this.phase = phase;
        this.notice = {
          needNotice,
          scheduledDate: row.scheduledDate ?? row.ScheduledDate ?? '',
          actionDeadline: row.actionDeadline ?? row.ActionDeadline ?? '',
          fromGrade: row.fromGrade ?? row.FromGrade ?? '',
          targetGrade: row.targetGrade ?? row.TargetGrade ?? '',
          targetRoleLimit: this.toQty(row.targetRoleLimit ?? row.TargetRoleLimit),
          currentRoleLimit: this.toQty(row.currentRoleLimit ?? row.CurrentRoleLimit),
          currentRoleUsed: this.toQty(row.currentRoleUsed ?? row.CurrentRoleUsed),
          currentProductQty: this.toQty(row.currentProductQty ?? row.CurrentProductQty),
          currentGalleryQty: this.toQty(row.currentGalleryQty ?? row.CurrentGalleryQty),
          targetProductQty: this.toQty(row.targetProductQty ?? row.TargetProductQty),
          targetGalleryQty: this.toQty(row.targetGalleryQty ?? row.TargetGalleryQty),
          excessCount: this.toQty(row.excessCount ?? row.ExcessCount),
          daysRemaining: this.toQty(row.daysRemaining ?? row.DaysRemaining),
          jobClearDays: this.toQty(row.jobClearDays ?? row.JobClearDays) || 7,
        };

        if (!needNotice || (phase !== 'Reminder' && phase !== 'Action')) {
          this.showModal = false;
          return;
        }
        /* Reminder：Later 仅挡住当前页；刷新会再弹。Action：每次进首页都提示 */
        if (phase === 'Reminder' && this.isReminderDismissed()) {
          this.showModal = false;
          return;
        }
        this.showModal = true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[EPO Notice] evaluate error:', e);
      } finally {
        this.loading = false;
      }
    },
    onLater() {
      if (this.phase === 'Reminder') {
        this.markReminderDismissed();
      }
      this.showModal = false;
    },
    onPrimary() {
      if (this.phase === 'Reminder') {
        this.markReminderDismissed();
      }
      this.showModal = false;
      if (this.phase === 'Action') {
        this.showSelectInactive = true;
        return;
      }
      this.$router.push({ path: this.myUsersPath }).catch(() => {});
    },
    onSelectInactiveSaved() {
      this.showSelectInactive = false;
      this.evaluate();
    },
  },
};
</script>

<style lang="scss" scoped>
/* Figma: Downgrade Notification Dialog 2434:222644 */
.epo-home-notice {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.epo-home-notice__scrim {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
}

.epo-home-notice__dialog {
  position: relative;
  width: 100%;
  max-width: 560px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.epo-home-notice__icon {
  width: 96px;
  height: 96px;
  object-fit: contain;
  flex-shrink: 0;
}

.epo-home-notice__content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.epo-home-notice__title {
  margin: 0;
  width: 100%;
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);
}

.epo-home-notice__body {
  margin: 0;
  width: 100%;
  text-align: left;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);

  ::v-deep strong {
    font-weight: 700;
    color: rgba(0, 0, 0, 0.8);
  }
}

.epo-home-notice__entitlements {
  width: 100%;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.epo-home-notice__ent-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.epo-home-notice__ent-label {
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.4);
}

.epo-home-notice__ent-line {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.8);

  & + & {
    margin-top: 4px;
  }
}

.epo-home-notice__ent-col .epo-home-notice__ent-line + .epo-home-notice__ent-line {
  margin-top: 12px;
}

.epo-home-notice__actions {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.epo-home-notice__later {
  appearance: none;
  border: none;
  background: transparent;
  padding: 15px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: -0.154px;
  color: rgba(0, 18, 26, 0.6);
  cursor: pointer;
}

.epo-home-notice__ok {
  appearance: none;
  border: none;
  background: #7366ff;
  color: #fff;
  padding: 15px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: -0.154px;
  cursor: pointer;
  min-width: 64px;
}
</style>
